import {
  Menu,
  MenuButton,
  MenuItems,
  type MenuItemsProps,
} from "@headlessui/react";
import { type MouseEvent, type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";

export interface DropDownMenuProps extends MenuItemsProps {
  button: ReactNode;
  size?: keyof typeof sizes;
  className?: string;
  wrapperClassName?: string;
  offset?: number;
  anchorToPointer?: boolean;
}

const sizes = {
  xs: "min-w-12",
  sm: "min-w-32",
  md: "min-w-40",
  lg: "min-w-52",
  auto: "min-w-[var(--button-width)]",
};

export default function DropDownMenu({
  button,
  size = "auto",
  offset = 8,
  className,
  wrapperClassName,
  anchor: anchorProp,
  anchorToPointer = false,
  children,
  ...rest
}: DropDownMenuProps) {
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const anchorStr = anchorProp ?? "bottom end";
  const anchorWithGap =
    typeof anchorStr === "string"
      ? { to: anchorStr as string, gap: offset }
      : { ...(anchorStr as object), gap: offset };

  return (
    <Menu as="div" className={cn("flex w-full", wrapperClassName)}>
      <MenuButton
        as="div"
        className="h-full w-full"
        onClick={
          anchorToPointer
            ? (event: MouseEvent<HTMLDivElement>) => {
                setPointer({ x: event.clientX, y: event.clientY });
              }
            : undefined
        }
      >
        {button}
      </MenuButton>
      <MenuItems
        transition
        portal={anchorToPointer || undefined}
        anchor={
          anchorToPointer
            ? undefined
            : (anchorWithGap as MenuItemsProps["anchor"])
        }
        style={
          anchorToPointer && pointer
            ? { position: "fixed", left: pointer.x, top: pointer.y }
            : undefined
        }
        className={cn(
          "origin-top-start z-20 rounded-lg border border-gray-200 bg-white p-1.5 text-sm text-gray-700 shadow-lg transition duration-100 ease-out focus:outline-hidden data-closed:scale-95 data-closed:opacity-0",
          sizes[size],
          className,
        )}
        {...rest}
      >
        {children}
      </MenuItems>
    </Menu>
  );
}
